import { onlineRacerName } from '@/config/racers';

/**
 * leaderboard.ts — the WORLD RANKINGS ladder (M78), pure + provider-wrapped.
 *
 * No accounts exist yet, so the board is a LOCAL ladder: a fixed curve of 19
 * named rivals (handles drawn deterministically from the online username pool,
 * the game's established "reads like the real world" language) with the
 * player's REAL Skill-Point total inserted at its true rank. Fixed rungs make
 * climbing legible — every rival you pass stays passed.
 *
 * SUPABASE-READY SEAM: all UI reads go through {@link LeaderboardProvider} —
 * an async interface returning a {@link LeaderboardView}. When accounts land,
 * a SupabaseLeaderboardProvider (select top N + the player's rank from a
 * `profiles` table, entry.id = auth user id) replaces LocalLeaderboardProvider
 * with zero UI changes. `id: null` marks a local ladder rival; real players
 * will carry their account id.
 */

export interface LeaderboardEntry {
  /** Account id (Supabase auth uid). null = a local-ladder rival. */
  id: string | null;
  name: string;
  points: number;
  isPlayer: boolean;
  /** M100 — the SERVER's rank for this row (ties share). Local-ladder rows
   *  omit it and the screen falls back to the row index. Lets the "You" row
   *  show its true world rank even when it sits outside the top N. */
  rank?: number;
}

export interface LeaderboardView {
  /** Best first; the player's row is always present. */
  rows: LeaderboardEntry[];
  /** The player's 1-based rank in `rows`. */
  playerRank: number;
}

/** Which board (M82): the career ladder, or the week's sprint (resets Monday
 *  00:00 ET — see core/skillPoints etWeekKey). */
export type LeaderboardKind = 'alltime' | 'week';

/** The fixed ladder rungs, ascending. Low rungs are close together so a new
 *  player's first few races visibly climb the board; the top is a long career.
 *  The single tuning knob for the local board's difficulty curve. */
export const LADDER_POINTS: readonly number[] = [
  10, 25, 45, 70, 105, 150, 210, 290, 390, 520, 680, 880, 1130, 1440, 1830, 2320, 2930, 3690, 4640,
];

/** The weekly board's rungs — a single week of racing (a win pays 15), so the
 *  curve is far flatter: passing rivals mid-week must feel reachable. */
export const LADDER_WEEK_POINTS: readonly number[] = [
  4, 9, 15, 22, 30, 40, 52, 66, 82, 100, 122, 148, 178, 214, 256, 306, 366, 438, 524,
];

/** Seeds the deterministic name walks — every install sees the same ladders
 *  (different key per board, so the two boards read as different fields). */
const LADDER_KEY = 'dr-world-rankings';
const LADDER_WEEK_KEY = 'dr-weekly-sprint';

/**
 * The local rankings board for a player total. Rung i (ascending) wears
 * the pool handle for slot i+1 — the prime-pool stride walk guarantees 19
 * distinct names. Ties rank the PLAYER first (friendly — matches the
 * championship tiebreak convention in Progression).
 */
export function localLeaderboard(playerPoints: number, kind: LeaderboardKind = 'alltime'): LeaderboardView {
  const rungs = kind === 'week' ? LADDER_WEEK_POINTS : LADDER_POINTS;
  const key = kind === 'week' ? LADDER_WEEK_KEY : LADDER_KEY;
  const rows: LeaderboardEntry[] = rungs.map((points, i) => ({
    id: null,
    name: onlineRacerName(i + 1, null, key),
    points,
    isPlayer: false,
  }));
  rows.push({ id: 'local', name: 'You', points: playerPoints, isPlayer: true });
  rows.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    return (b.isPlayer ? 1 : 0) - (a.isPlayer ? 1 : 0);
  });
  return { rows, playerRank: rows.findIndex((r) => r.isPlayer) + 1 };
}

/** The async seam UI code talks to (see the header — Supabase drops in here;
 *  the weekly board becomes a `point_events` SUM over the ET week window). */
export interface LeaderboardProvider {
  fetch(playerPoints: number, kind: LeaderboardKind): Promise<LeaderboardView>;
}

export class LocalLeaderboardProvider implements LeaderboardProvider {
  fetch(playerPoints: number, kind: LeaderboardKind): Promise<LeaderboardView> {
    return Promise.resolve(localLeaderboard(playerPoints, kind));
  }
}
